import type { ApiClient, ListResponse, Response } from "../../types";
import type { Payment, CreatePaymentPayload, PaymentFilters } from "./types";

/**
 * Query parameters for filtering payments
 */
export interface PaymentListParams extends PaymentFilters {
  limit?: number;
  offset?: number;
  /**
   * The selected fleet. Without it the server picks one of the user's fleets,
   * so a person in more than one fleet can get another fleet's answer.
   */
  fleet_id?: string;
}

export const payments = {
  /**
   * List payments with optional filters
   */
  async list(
    client: ApiClient,
    params?: PaymentListParams,
  ): Promise<ListResponse<Payment>> {
    const searchParams = new URLSearchParams();

    if (params?.rental_id) searchParams.append("rental_id", params.rental_id);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.payment_method_id)
      searchParams.append("payment_method_id", params.payment_method_id);
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.offset) searchParams.append("offset", params.offset.toString());
    if (params?.fleet_id) searchParams.append("fleet_id", params.fleet_id);

    const queryString = searchParams.toString();
    const url = `${client.baseUrl}/payments${queryString ? `?${queryString}` : ""}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (client.getAuthToken) {
      const token = await client.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch payments: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get a specific payment by ID
   */
  async get(client: ApiClient, id: string): Promise<Response<Payment>> {
    const url = `${client.baseUrl}/payments/${id}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (client.getAuthToken) {
      const token = await client.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch payment: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Create a new payment
   */
  async create(
    client: ApiClient,
    payload: CreatePaymentPayload,
  ): Promise<Response<Payment>> {
    const url = `${client.baseUrl}/payments`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (client.getAuthToken) {
      const token = await client.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ payment: payload }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create payment: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Confirm a payment using Stripe's async flow
   */
  async confirm(
    client: ApiClient,
    paymentId: string,
  ): Promise<Response<Payment>> {
    const url = `${client.baseUrl}/payments/${paymentId}/confirm`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (client.getAuthToken) {
      const token = await client.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to confirm payment: ${response.statusText}`);
    }

    return response.json();
  },
};
